import { motion, type TargetAndTransition } from "framer-motion";
import { useI18n } from "@/i18n";
import { hapticTap } from "@/tg/init";

interface CardProps {
  title: string;
  subtitle?: string | null;
  imageUrl?: string | null;
  date?: string | null;
  onClick?: () => void;
}

function formatDate(dateStr: string, locale: string): string {
  try {
    return new Date(dateStr).toLocaleDateString(locale, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

const tapAnim: TargetAndTransition = { scale: 0.97 };

export default function Card({ title, subtitle, imageUrl, date, onClick }: CardProps) {
  const { lang } = useI18n();

  function handleClick() {
    if (!onClick) return;
    hapticTap();
    onClick();
  }

  const tapProps = onClick ? { whileTap: tapAnim } : {};

  return (
    <motion.div
      {...tapProps}
      onClick={handleClick}
      className="rounded-2xl overflow-hidden mb-3 shadow-sm"
      style={{
        background: "var(--tg-secondary-bg)",
        cursor: onClick ? "pointer" : "default",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      {imageUrl && (
        <div className="w-full h-44 overflow-hidden">
          <img
            src={imageUrl}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover"
          />
        </div>
      )}
      <div className="px-4 py-3">
        <h3 className="font-semibold text-base leading-snug" style={{ color: "var(--tg-text)" }}>
          {title}
        </h3>
        {subtitle && (
          <p className="text-sm mt-1 line-clamp-2" style={{ color: "var(--tg-hint)" }}>
            {subtitle}
          </p>
        )}
        {date && (
          <p className="text-xs mt-2" style={{ color: "var(--tg-hint)" }}>
            {formatDate(date, lang === "uk" ? "uk-UA" : "en-US")}
          </p>
        )}
      </div>
    </motion.div>
  );
}
